import { useId, type InputHTMLAttributes, type ReactNode } from "react";

interface TextFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  hint?: string;
  error?: string;
  leadingIcon?: ReactNode;
  trailingAction?: ReactNode;
}

export function TextField({ label, hint, error, id, leadingIcon, trailingAction, ...rest }: TextFieldProps) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  const hintId = hint ? `${fieldId}-hint` : undefined;
  const errorId = error ? `${fieldId}-error` : undefined;

  return (
    <div className={["field", leadingIcon && "field--with-leading-icon", trailingAction && "field--with-trailing-action"].filter(Boolean).join(" ")}>
      <label className="field__label" htmlFor={fieldId}>
        {label}
      </label>
      <div className="field__control">
        {leadingIcon && <span className="field__leading-icon" aria-hidden="true">{leadingIcon}</span>}
        <input
          id={fieldId}
          className="field__input"
          aria-describedby={[hintId, errorId].filter(Boolean).join(" ") || undefined}
          aria-invalid={Boolean(error)}
          {...rest}
        />
        {trailingAction && <span className="field__trailing-action">{trailingAction}</span>}
      </div>
      {hint && (
        <span className="field__hint" id={hintId}>
          {hint}
        </span>
      )}
      {error && (
        <span className="field__error" id={errorId} role="alert">
          {error}
        </span>
      )}
    </div>
  );
}
